import { useState } from "react";
import { money } from "./shared";
import "./ThankYouBody.css";

/**
 * Shared thank-you content — order summary + login credentials — reused by all
 * 17 ThankYouTemplate{N}. Reuses each template's own card classes (`${p}-card`,
 * `${p}-sum`, `${p}-row`, `${p}-total`, `${p}-ch`, `${p}-note`) so it inherits
 * that family's styling; credential-specific bits use theme-driven `ty-*`.
 *
 * Props: `data` (thank-you payload), `p` (family class prefix, e.g. "cok1"),
 * `c` (resolved copy from shared.copy()).
 */
const GROUP_NOTE = {
  pending: "Your request to join was sent to the community admin. You'll get access once it's approved.",
  waitlisted: "This community is full, so you're on the waitlist. You'll be added when a spot opens.",
  not_added: "You couldn't be added to this community. Please contact the community admin.",
};

export default function ThankYouBody({ data = {}, p, c }) {
  const [copied, setCopied] = useState("");
  const isFree = !(Number(data.amount) > 0);
  const showPass = data.is_new && data.password;
  const total = isFree ? "Free" : money(data.amount, data.currency);
  // Groups the order delivers that did not open straight away (private group
  // awaiting approval, full group, email not on the authorized list).
  const groupNotes = (Array.isArray(data.groups) ? data.groups : [])
    .map((g) => ({ name: g.group_name || "the community", text: GROUP_NOTE[g.outcome] }))
    .filter((g) => g.text);

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className={`${p}-grid`}>
      {/* Credentials */}
      <div className={`${p}-card`}>
        <span className="ty-check" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <h3 className={`${p}-ch ty-h`}>{(data.headline || "").trim() || "You're enrolled!"}</h3>
        <p className="ty-lead">
          Your access to <strong>{data.course_name || c.label}</strong> is confirmed.
        </p>

        {groupNotes.map((g) => (
          <p key={g.name} className="ty-warn">
            <strong>{g.name}:</strong> {g.text}
          </p>
        ))}

        <div className="ty-cred">
          <p className="ty-cred-title">Your login details</p>
          {showPass ? (
            <p className="ty-warn">Save these now — the password is shown only once.</p>
          ) : (
            <p className="ty-muted">Log in with the password for your existing account.</p>
          )}

          <div className="ty-row">
            <div className="ty-kv">
              <span className="ty-k">Email</span>
              <span className="ty-v">{data.email}</span>
            </div>
            <button type="button" className="ty-copy" onClick={() => copy("email", data.email)}>
              {copied === "email" ? "Copied" : "Copy"}
            </button>
          </div>

          {showPass ? (
            <div className="ty-row">
              <div className="ty-kv">
                <span className="ty-k">Password</span>
                <span className="ty-v ty-mono">{data.password}</span>
              </div>
              <button type="button" className="ty-copy" onClick={() => copy("pass", data.password)}>
                {copied === "pass" ? "Copied" : "Copy"}
              </button>
            </div>
          ) : null}

          {data.login_url ? (
            <a href={data.login_url} className={`${p}-pay ty-login`}>
              Go to your login →
            </a>
          ) : null}
        </div>
      </div>

      {/* Order summary */}
      <aside className={`${p}-card ${p}-sum`}>
        <h3 className={`${p}-ch`}>Order summary</h3>
        <div className={`${p}-row`}>
          <span>{data.course_name || c.label}</span>
          <strong>{total}</strong>
        </div>
        <div className={`${p}-row`}>
          <span>Order</span>
          <strong className="ty-mono">{data.order_id}</strong>
        </div>
        <div className={`${p}-row ${p}-total`}>
          <span>Total paid</span>
          <span>{total}</span>
        </div>
        <p className={`${p}-note`}>🔒 A copy is saved to your account.</p>
      </aside>
    </div>
  );
}
